import { ChevronDown, LogOut, ShieldCheck, UserRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Avatar, AvatarFallback } from '~/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu'
import { useAuth } from '~/helpers/store/auth'
import { useStatsNoticeDialog } from '~/helpers/store/consent'
import { tourAnchor } from '~/helpers/tour/anchors'
import { userDisplayName } from '~/helpers/users/display-name'
import { userInitials } from '~/helpers/users/initials'
import { accountPathOf, helpPathOf } from '~/helpers/users/space-paths'
import HelpMenuItems from '~/pages/help/components/HelpMenuItems'
import LegalMenuItems from './LegalMenuItems'

/**
 * Menu déroulant apprenant partagé entre les headers « Mes sessions » et
 * « Lecteur SCORM » pour un rendu identique partout. Regroupe l'identité, la
 * langue, le thème, l'accès à la gestion des données personnelles (RGPD) et la
 * déconnexion — le même jeu de réglages que le menu du back-office.
 *
 * Le contenu SCORM garde sa propre langue : changer celle de l'interface ne
 * traduit pas les modules, qui sont des paquets figés livrés par l'organisme.
 *
 * Plus de variante « sombre » : elle existait pour un lecteur SCORM peint en dur
 * en noir, qui ignorait la bascule de thème rendue par ce menu même. Le cadre
 * suivant désormais le thème, une seconde palette n'aurait plus rien à habiller.
 */
function LearnerUserMenu() {
  const { t } = useTranslation()
  const { payload } = useAuth()
  const navigate = useNavigate()
  const openStatsNotice = useStatsNoticeDialog(s => s.open)

  if (!payload) return null

  const initials = userInitials(payload.firstName, payload.lastName, payload.username)

  return (
    <DropdownMenu>
      {/* Survol en couple accent / encre d'accent, garanti par le branding : le
          seul fond changeait, et en sombre l'encre claire du thème tombait sur
          l'accent clair — le nom devenait illisible. */}
      <DropdownMenuTrigger {...tourAnchor('learner.menu')} className="group flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-background p-1 sm:px-3 sm:py-1.5 text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
        <Avatar className="size-7 rounded-full">
          <AvatarFallback className="rounded-full text-xs">{initials}</AvatarFallback>
        </Avatar>
        {/* Le nom passe à la trappe sur téléphone : dans le bandeau du lecteur,
            il disputait sa place au titre du module, qui compte davantage. */}
        {/* `not-sr-only` remet `white-space: normal` : sans le `nowrap` qui suit,
            un nom long passait sur trois lignes et gonflait la barre (#307). */}
        <span className="sr-only max-w-40 truncate text-sm font-medium sm:not-sr-only sm:whitespace-nowrap">
          {userDisplayName(payload)}
        </span>
        {/* Sur téléphone, les initiales seules : le chevron prenait la place du logo (#307). */}
        <ChevronDown className="hidden size-4 text-muted-foreground group-hover:text-accent-foreground sm:block" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52 rounded-lg">
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-2 py-1.5 text-sm">
            <Avatar className="size-8 rounded-full">
              <AvatarFallback className="rounded-full">{initials}</AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">{userDisplayName(payload)}</span>
              {payload.email && <span className="truncate text-xs text-muted-foreground">{payload.email}</span>}
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* Le seul point de bascule qu'avait l'apprenant était l'écran de
            connexion (#48) : une fois connecté, il ne pouvait plus changer de
            langue avant sa prochaine déconnexion. Calqué sur `UserNav.tsx`
            pour que les deux menus du produit se ressemblent. */}
        <DropdownMenuItem onClick={() => navigate(accountPathOf(payload.role))} className="cursor-pointer">
          <UserRound className="mr-2 size-4" />
          {t('account.title')}
        </DropdownMenuItem>
        <HelpMenuItems helpPath={helpPathOf(payload.role)} />
        <DropdownMenuItem onClick={openStatsNotice} className="cursor-pointer">
          <ShieldCheck className="mr-2 size-4" />
          {t('notice.stats.manage')}
        </DropdownMenuItem>
        <LegalMenuItems />
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/logout')} className="cursor-pointer">
          <LogOut className="mr-2 size-4" />
          {t('common.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default LearnerUserMenu
